import BaseAuthProvider from './BaseAuthProvider'
import {AuthProviders} from '@/domain/login/constant/authProviders'

/**
 * LDAP authentication provider
 * Supports username/password auth with JWT access/refresh tokens
 */
export default class LdapAuthProvider extends BaseAuthProvider {
    /**
     * Authenticate with LDAP credentials
     * @param {Object} credentials - {username, password}
     * @returns {Promise<Object>} {token: access_token, type: 'Bearer', refresh_token, jwt: access_token, expires_in, refresh_expires_in}
     */
    async authenticate(credentials) {
        const {username, password} = credentials

        const result = await this.service.authenticateLdap(username, password)

        return {
            token: result.access_token,
            type: result.token_type || 'Bearer',
            refresh_token: result.refresh_token,
            jwt: result.access_token, // For LDAP, JWT is the access token itself
            expires_in: result.expires_in,
            refresh_expires_in: result.refresh_expires_in
        }
    }

    /**
     * Refresh LDAP access token using refresh token
     * @param {Object} tokenData - {refresh_token}
     * @returns {Promise<Object>} New token data with rotated refresh_token
     */
    async refreshToken(tokenData) {
        const {refresh_token} = tokenData

        if (!refresh_token) {
            throw new Error('No refresh token available')
        }

        const result = await this.service.refreshLdapToken(refresh_token)

        return {
            token: result.access_token,
            type: result.token_type || 'Bearer',
            refresh_token: result.refresh_token, // New refresh token (rotation)
            jwt: result.access_token,
            expires_in: result.expires_in,
            refresh_expires_in: result.refresh_expires_in
        }
    }

    /**
     * Logout and revoke LDAP tokens on server
     * @param {Object} tokenData - {refresh_token}
     * @param {boolean} revokeAllDevices - Revoke tokens on all devices
     * @returns {Promise<void>}
     */
    async logout(tokenData, revokeAllDevices = false) {
        const {refresh_token} = tokenData

        if (!refresh_token) {
            // No refresh token to revoke, just cleanup locally
            return
        }

        try {
            await this.service.revokeLdapToken(refresh_token, revokeAllDevices)
        } catch (e) {
            // Log error but don't fail logout
            console.warn('LDAP token revocation failed:', e)
        }
    }

    supportsRefresh() {
        return true
    }

    getMethod() {
        return AuthProviders.LDAP
    }

    /**
     * LDAP alternative: password (if enabled)
     */
    getAlternatives(isEnabled) {
        return []
    }
}
